import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Camera, FileText, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";

const OPTIONS = [
  ["si", "Sí", "bg-emerald-400 text-[#0B0F17] border-emerald-400"],
  ["no", "No", "bg-rose-500 text-white border-rose-500"],
  ["na", "N/A", "bg-[#F5F1E8] text-[#0B0F17] border-[#F5F1E8]"],
];

const Evidence = ({ q, value, onChange }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Selecciona una imagen");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      onChange({ ...value, photo_path: data.path });
      toast.success("Evidencia cargada");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }} className="overflow-hidden">
      <div className="mt-5 border border-dashed border-rose-400/50 bg-rose-500/5 p-4 flex items-center gap-4">
        {value.photo_path ? (
          <div className="relative w-24 h-24 shrink-0 overflow-hidden" data-testid={`evidence-preview-${q.id}`}>
            <img src={fileUrl(value.photo_path)} alt="Evidencia" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange({ ...value, photo_path: null })} data-testid={`evidence-remove-${q.id}`} aria-label="Quitar foto" className="absolute top-1 right-1 p-1 bg-[#0B0F17]/80 rounded-full"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`evidence-upload-${q.id}`} className="w-24 h-24 shrink-0 grid place-items-center border border-white/15 hover:border-rose-400 transition-colors">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-6 h-6 text-rose-400" />}
          </button>
        )}
        <div className="text-sm">
          <div className="font-semibold text-rose-300">Incumplimiento detectado · <span className="text-rose-200">foto obligatoria</span></div>
          <div className="text-[#F5F1E8]/70">{value.photo_path ? "Foto de evidencia adjunta." : "Sube una foto del espacio o equipo que presenta la falla (JPG/PNG, máx. 8 MB)."}</div>
        </div>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid={`evidence-input-${q.id}`} onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </motion.div>
  );
};

const DocNote = ({ q }) => (
  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.4 }} className="overflow-hidden">
    <div className="mt-5 border border-sky-400/40 bg-sky-400/5 p-4 flex items-start gap-3 text-sm" data-testid={`documental-note-${q.id}`}>
      <FileText className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
      <div>
        <div className="font-semibold text-sky-200">Hallazgo documental · no requiere foto</div>
        <div className="text-[#F5F1E8]/70">Se registrará directamente en el plan de acción. Podrás cerrarlo después subiendo el documento de respaldo (PDF o imagen del acta, nombramiento o programa).</div>
      </div>
    </div>
  </motion.div>
);

export const QuestionCard = ({ q, index, value, onChange }) => (
  <div className="border-b border-white/10 py-8" data-testid={`question-${q.id}`}>
    <div className="grid grid-cols-12 gap-4">
      <span className="col-span-2 sm:col-span-1 font-mono text-sm text-[#F59E0B] pt-1">{String(index).padStart(2, "0")}</span>
      <div className="col-span-10 sm:col-span-11">
        <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.16em]">
          {q.topic && <span className="text-[#F5F1E8]/55" data-testid={`question-topic-${q.id}`}>{q.topic}</span>}
          <span className={q.kind === "documental" ? "text-sky-300/90" : "text-[#F5F1E8]/45"} data-testid={`question-kind-${q.id}`}>{q.kind === "documental" ? "Documental" : "Física / operativa"}</span>
        </div>
        <p className="text-lg sm:text-xl font-medium leading-snug">{q.text}</p>
        <div className="mt-5 flex flex-wrap gap-2">
          {OPTIONS.map(([v, label, active]) => (
            <motion.button
              key={v}
              type="button"
              whileTap={{ scale: 0.94 }}
              onClick={() => onChange({ answer: v, photo_path: v === "no" ? value?.photo_path ?? null : null })}
              data-testid={`question-${q.id}-${v}`}
              aria-pressed={value?.answer === v}
              className={`min-w-[84px] px-5 py-2.5 rounded-full border font-semibold text-sm transition-colors ${value?.answer === v ? active : "border-white/20 text-[#F5F1E8]/85 hover:border-white/50"}`}
            >
              {label}
            </motion.button>
          ))}
        </div>
        <AnimatePresence initial={false}>
          {value?.answer === "no" && (q.kind === "documental" ? <DocNote key="doc" q={q} /> : <Evidence key="ev" q={q} value={value} onChange={onChange} />)}
        </AnimatePresence>
      </div>
    </div>
  </div>
);
